// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2026 Vic Zhu <viczhu2014@gmail.com>

/**
 * HiddenPetsPanel — "Show hidden" view for patients a vet clinic-hid earlier
 * (vic/vetpo#12, follow-up of #10). #10 shipped only a same-session Undo
 * snackbar because no role could list its own hidden pets; vic/ada-core#150
 * closed that gap with a vet-reachable read path, `GET
 * /api/pets/clinic-hidden` -> `{ hidden: [{ petId, name, species, ownerName,
 * hiddenAt, hiddenBy }] }`. This panel reads that endpoint and restores a
 * row through the same `POST /api/pets/:id/clinic-unhide` HidePetButton's
 * Undo already calls.
 *
 * Role gate matches HidePetButton: the backend 403s anyone whose role isn't
 * EXACTLY 'vet' (clinicHidden.ts, ada-core — vetadmin's visibility doesn't
 * come from the master/sub clinic-hide circle, so it has nothing to list).
 * The callers (PetList's toggle, the dashboard's "Hidden patients" dialog)
 * are expected to only mount this for role 'vet'; this
 * component doesn't re-check identity itself so it stays a plain data view.
 */
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { useNotify, useRefresh, useTranslate } from 'react-admin'
import { deadlineFetch } from '../hooks/customEndpointFetch'
import { authHeaders } from '../utils/api'

interface HiddenPetRow {
  petId: number
  name: string | null
  species: string | null
  ownerName: string | null
  hiddenAt: string | null
  hiddenBy: string | null
}

const HiddenPetsPanel = () => {
  const translate = useTranslate()
  const notify = useNotify()
  const refresh = useRefresh()
  const [rows, setRows] = useState<HiddenPetRow[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [unhidingId, setUnhidingId] = useState<number | null>(null)
  // Read through a ref rather than a useCallback/useEffect dependency: the
  // mocked useTranslate in tests (and, in principle, a locale switch) can
  // hand back a new function identity per render, and this effect must run
  // once on mount, not on every render that happens to produce a new
  // translate reference.
  const translateRef = useRef(translate)
  translateRef.current = translate

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    deadlineFetch('/api/pets/clinic-hidden', { headers: authHeaders() })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}))
        if (cancelled) return
        if (response.status === 200 && Array.isArray(data?.hidden)) {
          setRows(data.hidden)
        } else {
          setRows([])
          setError(
            data?.errors?.[0]?.message ||
              translateRef.current('resources.pets.hide.hidden_list_error'),
          )
        }
      })
      .catch(() => {
        if (cancelled) return
        setRows([])
        setError(translateRef.current('resources.pets.hide.hidden_list_error'))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const handleUnhide = async (petId: number) => {
    setUnhidingId(petId)
    try {
      const response = await deadlineFetch(`/api/pets/${petId}/clinic-unhide`, {
        method: 'POST',
        headers: authHeaders(),
      })
      const data = await response.json().catch(() => ({}))
      if (response.status === 200 && data?.success) {
        notify(translate('resources.pets.hide.undo_success'), { type: 'success' })
        // Remove locally rather than a full reload — the row's gone from
        // the hidden set the moment clinic-unhide succeeds, and a reload
        // would also re-run the request the row just satisfied.
        setRows((prev) => (prev ? prev.filter((row) => row.petId !== petId) : prev))
        // ...but the patient lists behind this panel (the dashboard, PetList)
        // are react-query caches that still lack the pet: invalidate them so
        // the restored patient reappears without a page reload (#13).
        refresh()
      } else {
        notify(data?.errors?.[0]?.message || translate('resources.pets.hide.error'), {
          type: 'error',
        })
      }
    } catch (_e) {
      notify(translate('resources.pets.hide.error'), { type: 'error' })
    } finally {
      setUnhidingId(null)
    }
  }

  if (loading && rows === null) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress size={28} />
      </Box>
    )
  }

  if (error) {
    return (
      <Box p={2}>
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      </Box>
    )
  }

  if (!rows || rows.length === 0) {
    return (
      <Box p={2}>
        <Typography variant="body2" color="text.secondary">
          {translate('resources.pets.hide.hidden_list_empty')}
        </Typography>
      </Box>
    )
  }

  return (
    <Table data-testid="hidden-pets-table">
      <TableHead>
        <TableRow>
          <TableCell>{translate('resources.pets.hide.hidden_list_col_pet')}</TableCell>
          <TableCell>{translate('resources.pets.hide.hidden_list_col_owner')}</TableCell>
          <TableCell>{translate('resources.pets.hide.hidden_list_col_hidden_at')}</TableCell>
          <TableCell>{translate('resources.pets.hide.hidden_list_col_hidden_by')}</TableCell>
          <TableCell>{translate('resources.pets.hide.hidden_list_col_actions')}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.petId} data-testid={`hidden-pet-row-${row.petId}`}>
            <TableCell>
              {row.name || `#${row.petId}`}{' '}
              <Chip
                label={translate('resources.pets.hide.hidden_chip')}
                size="small"
                variant="outlined"
                sx={{ ml: 1 }}
              />
            </TableCell>
            <TableCell>{row.ownerName || '—'}</TableCell>
            <TableCell>{row.hiddenAt ? new Date(row.hiddenAt).toLocaleString() : '—'}</TableCell>
            <TableCell>{row.hiddenBy || '—'}</TableCell>
            <TableCell>
              <Button
                size="small"
                disabled={unhidingId === row.petId}
                onClick={() => handleUnhide(row.petId)}
              >
                {translate('resources.pets.hide.unhide_action')}
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

export default HiddenPetsPanel
